/* External */
import React, {
  Fragment,
  useEffect, useState, useRef, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

/* Ott-common */
import InfiniteScrollContainer from '@dtvgo/infinite-scroll-container';
import { gtmEvent } from '@dtvgo/gtm-event-report';

/* Actions */
import { hideLoading, showLoading } from 'react-redux-loading-bar';

/* Components */
import LanderLogo from '../../components/landerLogo/LanderLogo';
import LanderTitle from '../../components/landerTitle/LanderTitle';
import Carousel from '../../components/carousel/Carousel';
import SliderLoader from '../../components/sliderLoader/SliderLoader';
import HomeBannerSlot from './HomeBannerSlot';
import ToastBannerSlot from './ToastBannerSlot';
import { useGtmContext } from '../../components/GtmContext';
import { gtmDimensions } from '../../components/GtmContext/dimensions/header-footer';

/* Other */
import {
  CAROUSEL_TYPES,
  LANDER_URL,
  LANDERS_IDS,
  PAGE_SIZE,
  featuredCarouselTypes,
  formatContentForGtm,
  getPageQueryByFeatureFlag,
  gtmContentLander,
} from '../../utils';
import { isLanderBannerActive, isToastBannerActive } from '../../utils/env';
import { api } from '../../state/configureStore';

import './LanderHeroCarouselStack.scss';

const TOAST_ROW_PLACEHOLDER = { __toastBannerRowPlaceholder: true };

const LANDER_TOAST_HERO_STACK_TYPES = new Set([
  ...featuredCarouselTypes,
  CAROUSEL_TYPES.LANDSCAPE,
]);

function isToastHeroStackCarouselType(type) {
  return Boolean(type && LANDER_TOAST_HERO_STACK_TYPES.has(type));
}

function ToastBannerRowSkip({ onFetched }) {
  useEffect(() => {
    onFetched([TOAST_ROW_PLACEHOLDER]);
  }, [onFetched]);
  return null;
}

ToastBannerRowSkip.propTypes = {
  onFetched: PropTypes.func.isRequired,
};

function noop() {}

function getLanderElementId(el) {
  return el?.id ?? el?.elementId;
}

function Lander({
  titleId, pageId: pageIdFromProps, showLogo, children,
}) {
  const { landerUri, pageId: pageIdFromRoute } = useParams();
  const pageId = pageIdFromRoute || pageIdFromProps;
  const isMainHomeLander = pageId === LANDERS_IDS.HOME;
  const homeBannerFetchEnabled = isLanderBannerActive && isMainHomeLander;
  const toastBannerFetchEnabled = isToastBannerActive && isMainHomeLander;
  const useGetPageQuery = getPageQueryByFeatureFlag(api);
  const { data: { elements } = {}, isError: isPageError } = useGetPageQuery(
    { id: pageId },
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const successfulElementsCountRef = useRef(0);
  const resolveCarouselsFetchingPromiseRef = useRef();
  const [carouselsToFetchQuantity, setCarouselsToFetchQuantity] = useState(0);
  const [shownCarousels, setShownCarousels] = useState([]);
  const gtmRef = useRef(false);
  const { gtmUserData, setGtmProviderName } = useGtmContext();

  const firstToastBannerElementId = useMemo(() => {
    const t = elements?.find((el) => el.type === CAROUSEL_TYPES.TOAST_BANNER);
    return getLanderElementId(t);
  }, [elements]);

  const pinnedToastLayout = useMemo(
    () => Boolean(toastBannerFetchEnabled && firstToastBannerElementId),
    [toastBannerFetchEnabled, firstToastBannerElementId],
  );

  const toastPinnedBeforeRows = useMemo(
    () => Boolean(
      pinnedToastLayout
      && elements?.[0]?.type === CAROUSEL_TYPES.TOAST_BANNER,
    ),
    [pinnedToastLayout, elements],
  );

  const showPinnedToastAfterFirstRow = useMemo(
    () => Boolean(
      pinnedToastLayout
      && !toastPinnedBeforeRows
      && elements?.length,
    ),
    [pinnedToastLayout, toastPinnedBeforeRows, elements],
  );

  const fetchCarouselsBatch = useCallback(() => {
    dispatch(showLoading());

    const fetchingCarouselsPromise = new Promise((resolve) => {
      resolveCarouselsFetchingPromiseRef.current = resolve;
    });

    setCarouselsToFetchQuantity((prevQuantity) => prevQuantity + PAGE_SIZE);

    return fetchingCarouselsPromise;
  }, [dispatch]);

  useEffect(() => {
    fetchCarouselsBatch();
  }, [fetchCarouselsBatch]);

  useEffect(() => {
    if (isPageError) {
      dispatch(hideLoading());
      history.replace('/404');
    }
  }, [history, isPageError, dispatch]);

  useEffect(() => {
    if (landerUri) {
      localStorage.setItem(LANDER_URL, `/home/${landerUri}`);
    }
  }, [landerUri]);

  const handleCarouselFetched = useCallback((assets) => {
    const handleFinishCarouselFetch = () => {
      setShownCarousels(elements.slice(0, carouselsToFetchQuantity).map(({ id }) => id));
      resolveCarouselsFetchingPromiseRef.current();
      dispatch(hideLoading());
    };

    if (!elements?.length) {
      return;
    }

    if (carouselsToFetchQuantity >= elements.length) {
      handleFinishCarouselFetch();
    }

    if (!assets || assets.length <= 0) {
      setCarouselsToFetchQuantity((prevQuantity) => prevQuantity + 1);
      return;
    }

    successfulElementsCountRef.current += 1;

    if (
      successfulElementsCountRef.current < PAGE_SIZE
      && carouselsToFetchQuantity < elements.length
    ) return;

    successfulElementsCountRef.current = 0;

    handleFinishCarouselFetch();
  }, [elements, carouselsToFetchQuantity, dispatch]);

  useEffect(() => {
    let providerName = null;
    let landerData = landerUri ? gtmDimensions[landerUri] : null;

    if (pageIdFromRoute) {
      landerData = gtmContentLander.has(pageIdFromRoute)
        ? gtmDimensions.content : gtmDimensions.provider;

      providerName = gtmContentLander.has(pageIdFromRoute) ? null : pageIdFromRoute;
      setGtmProviderName(providerName);
    }

    if (!gtmRef.current && landerData) {
      gtmRef.current = true;

      const { hash, eventType, dimensions } = landerData.page_view;

      const dataTosend = formatContentForGtm(
        {},
        {
          providerName,
        },
      );

      gtmEvent({
        hash,
        eventType,
        dimensions,
        userData: gtmUserData,
        data: dataTosend,
      });
    }
  }, [landerUri, pageIdFromRoute, gtmUserData, setGtmProviderName]);

  return (
    <>
      {shownCarousels.length === 0 && <SliderLoader classCard="card-2X3" />}
      {showLogo && <LanderLogo pageId={pageId} />}
      {titleId && <LanderTitle titleId={titleId} />}

      {elements?.length && (
        <InfiniteScrollContainer
          onBottomReached={fetchCarouselsBatch}
          shouldObserveBottom={
            shownCarousels.length > 0
            && carouselsToFetchQuantity < elements.length
          }
        >
          {elements.slice(0, carouselsToFetchQuantity).map((el, index, arr) => {
            const id = getLanderElementId(el);
            const {
              source, type, filters, recommendationType, isCheddar,
            } = el;
            const prev = arr[index - 1];
            const next = arr[index + 1];

            const isPinnedHeroOverlayType = Boolean(
              type === CAROUSEL_TYPES.HOME_BANNER
              || isToastHeroStackCarouselType(type),
            );

            const wrapPinnedToastAfterFirstContentRow = (row) => {
              if (!firstToastBannerElementId || !toastBannerFetchEnabled) {
                return row;
              }

              const pinnedToastSlot = (
                <ToastBannerSlot
                  carouselId={String(firstToastBannerElementId)}
                  fetchEnabled={toastBannerFetchEnabled}
                  onFetched={noop}
                />
              );

              const overlayAfterFirstContent = showPinnedToastAfterFirstRow
                && index === 0
                && isPinnedHeroOverlayType;

              const overlayAfterLeadToast = toastPinnedBeforeRows
                && index === 1
                && isPinnedHeroOverlayType;

              if (overlayAfterFirstContent || overlayAfterLeadToast) {
                return (
                  <div
                    key={`pinned-hero-toast-${id}`}
                    className="dtv-lander-hero-carousel-stack"
                  >
                    {row}
                    <div className="dtv-lander-hero-carousel-stack__toast-layer">
                      {pinnedToastSlot}
                    </div>
                  </div>
                );
              }

              if (showPinnedToastAfterFirstRow && index === 0) {
                return (
                  <Fragment key={`pinned-after-${id}`}>
                    {row}
                    {pinnedToastSlot}
                  </Fragment>
                );
              }

              if (toastPinnedBeforeRows && index === 1 && !isPinnedHeroOverlayType) {
                return (
                  <Fragment key={`pinned-after-lead-${id}`}>
                    {pinnedToastSlot}
                    {row}
                  </Fragment>
                );
              }

              return row;
            };

            if (type === CAROUSEL_TYPES.TOAST_BANNER) {
              if (String(id) !== String(firstToastBannerElementId)) {
                return (
                  <ToastBannerRowSkip
                    key={`toast-skip-${id}`}
                    onFetched={handleCarouselFetched}
                  />
                );
              }
              if (pinnedToastLayout) {
                return (
                  <ToastBannerRowSkip
                    key={`toast-skip-pinned-${id}`}
                    onFetched={handleCarouselFetched}
                  />
                );
              }
              if (!pinnedToastLayout && next?.type === CAROUSEL_TYPES.HOME_BANNER) {
                const nextId = getLanderElementId(next);
                return (
                  <HomeBannerSlot
                    key={`hero-toast-${id}-${nextId}`}
                    carouselId={String(nextId)}
                    fetchEnabled={homeBannerFetchEnabled}
                    onFetched={handleCarouselFetched}
                    heroToastOverlay={(
                      <ToastBannerSlot
                        carouselId={String(id)}
                        fetchEnabled={toastBannerFetchEnabled}
                        onFetched={handleCarouselFetched}
                      />
                    )}
                  />
                );
              }
              if (!pinnedToastLayout && next && isToastHeroStackCarouselType(next.type)) {
                const nextId = getLanderElementId(next);
                return (
                  <div
                    key={`hero-toast-${id}-${nextId}`}
                    className="dtv-lander-hero-carousel-stack"
                  >
                    <Carousel
                      id={nextId}
                      key={nextId}
                      landerUri={landerUri || pageIdFromRoute}
                      onFetched={handleCarouselFetched}
                      shown={shownCarousels.includes(String(nextId))}
                      source={next.source}
                      type={next.type}
                      filters={next.filters}
                      carouselVerticalPosition={index + 2}
                      recommendationType={next.recommendationType}
                      isCheddar={next.isCheddar}
                    />
                    <div className="dtv-lander-hero-carousel-stack__toast-layer">
                      <ToastBannerSlot
                        carouselId={String(id)}
                        fetchEnabled={toastBannerFetchEnabled}
                        onFetched={handleCarouselFetched}
                      />
                    </div>
                  </div>
                );
              }
              if (!pinnedToastLayout && prev?.type === CAROUSEL_TYPES.HOME_BANNER) {
                return <Fragment key={id} />;
              }
              if (!pinnedToastLayout && prev && isToastHeroStackCarouselType(prev.type)) {
                return <Fragment key={id} />;
              }
              return (
                <ToastBannerSlot
                  key={id}
                  carouselId={String(id)}
                  fetchEnabled={toastBannerFetchEnabled}
                  onFetched={handleCarouselFetched}
                />
              );
            }

            if (type === CAROUSEL_TYPES.HOME_BANNER) {
              const prevId = getLanderElementId(prev);
              const nextId = getLanderElementId(next);
              if (
                !pinnedToastLayout
                && prev?.type === CAROUSEL_TYPES.TOAST_BANNER
                && String(prevId) === String(firstToastBannerElementId)
              ) {
                return <Fragment key={id} />;
              }
              if (
                !pinnedToastLayout
                && next?.type === CAROUSEL_TYPES.TOAST_BANNER
                && String(nextId) === String(firstToastBannerElementId)
              ) {
                return (
                  <HomeBannerSlot
                    key={`hero-toast-${id}-${nextId}`}
                    carouselId={String(id)}
                    fetchEnabled={homeBannerFetchEnabled}
                    onFetched={handleCarouselFetched}
                    heroToastOverlay={(
                      <ToastBannerSlot
                        carouselId={String(nextId)}
                        fetchEnabled={toastBannerFetchEnabled}
                        onFetched={handleCarouselFetched}
                      />
                    )}
                  />
                );
              }
              return wrapPinnedToastAfterFirstContentRow(
                <HomeBannerSlot
                  key={id}
                  carouselId={String(id)}
                  fetchEnabled={homeBannerFetchEnabled}
                  onFetched={handleCarouselFetched}
                />,
              );
            }

            if (
              !pinnedToastLayout
              && prev?.type === CAROUSEL_TYPES.TOAST_BANNER
              && String(getLanderElementId(prev)) === String(firstToastBannerElementId)
              && isToastHeroStackCarouselType(type)
            ) {
              return <Fragment key={id} />;
            }

            if (
              !pinnedToastLayout
              && next?.type === CAROUSEL_TYPES.TOAST_BANNER
              && String(getLanderElementId(next)) === String(firstToastBannerElementId)
              && isToastHeroStackCarouselType(type)
            ) {
              const nextId = getLanderElementId(next);
              return (
                <div
                  key={`hero-toast-${id}-${nextId}`}
                  className="dtv-lander-hero-carousel-stack"
                >
                  <Carousel
                    id={id}
                    key={id}
                    landerUri={landerUri || pageIdFromRoute}
                    onFetched={handleCarouselFetched}
                    shown={shownCarousels.includes(String(id))}
                    source={source}
                    type={type}
                    filters={filters}
                    carouselVerticalPosition={index + 1}
                    recommendationType={recommendationType}
                    isCheddar={isCheddar}
                  />
                  <div className="dtv-lander-hero-carousel-stack__toast-layer">
                    <ToastBannerSlot
                      carouselId={String(nextId)}
                      fetchEnabled={toastBannerFetchEnabled}
                      onFetched={handleCarouselFetched}
                    />
                  </div>
                </div>
              );
            }

            return wrapPinnedToastAfterFirstContentRow(
              <Carousel
                id={id}
                key={id}
                landerUri={landerUri || pageIdFromRoute}
                onFetched={handleCarouselFetched}
                shown={shownCarousels.includes(String(id))}
                source={source}
                type={type}
                filters={filters}
                carouselVerticalPosition={index + 1}
                recommendationType={recommendationType}
                isCheddar={isCheddar}
              />,
            );
          })}
        </InfiniteScrollContainer>
      )}

      {children}
    </>
  );
}

Lander.propTypes = {
  titleId: PropTypes.string,
  pageId: PropTypes.string,
  showLogo: PropTypes.bool,
  children: PropTypes.node,
};

Lander.defaultProps = {
  titleId: '',
  pageId: '',
  showLogo: false,
  children: undefined,
};

export default Lander;
